import {
  ActionIcon,
  Box,
  Center,
  CloseButton,
  Group,
  Skeleton,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useFullscreen } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconArrowsMaximize,
  IconRectangle,
  IconSquareToggle,
} from "@tabler/icons-react";
import BottomTray from "core/components/approval/BottomTray";
import SingleLayout from "core/components/approval/SingleLayout";
import { CaptureEventType } from "core/components/capture/CaptureThumb";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AppConstants,
  ApprovalImageToolbarModes,
  ApprovalLayoutTypes,
  ComparisonOperators,
  MarkupEventTypes,
} from "utils/constants";
import { logger } from "utils/logger";
import AssetInfoPanel from "./AssetInfoPanel";
import classes from "./AssetViewer.module.scss";
import { PrimaryBadge } from "core/components/badge/PrimaryBadge";
import Toolbar from "./Toolbar";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import CompareLayout from "./CompareLayout";
import FilterPanel from "./FilterPanel";
import { useForm } from "@mantine/form";
import * as utils from "utils/helper";
import { AnnotationModel, MediaFilterModel } from "core/models";
import CommentLayout from "./CommentLayout";

const AssetViewer = ({
  style,
  index = 0,
  onClose,
  onCaptureChange,
  showSettings = false,
  sessions = [],
  approvalType = null,
  approvalFilter = new MediaFilterModel(),
  onFilterChange = () => {},
  user = null,
  onMarkupChange = () => {},
  allowCommenting = false,
  showComments = true,
  showFilters = false,
  onImageEvent = () => {},
  assetType = "task",
}) => {
  const [comparisonItems, setComparisonItems] = useState([
    style?.assets?.[index],
  ]);

  const [selectedCompareFrame, setSelectedCompareFrame] = useState(0);
  const [imageControlSettings, setImageControlSettings] = useState({
    zoomDirection: 1,
  });
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [assets, setAssets] = useState([]);
  const location = useLocation();
  const params = useParams();

  // const [approvalSettings, setApprovalSettings] = useState({
  //   layoutType: ApprovalLayoutTypes.SINGLE,
  //   infoOpen: false,
  //   filterOpen: false,
  //   thumbnailSize: 150,
  //   isCommenting: false,
  //   sortBy: "actionSort",
  //   sortDirection: "asc",
  //   syncZoomPan: true,
  // });

  useEffect(() => {
    filterMedia(approvalFilter);
    // setAssets(style?.assets??[]);
  }, [style?.assets]);

  // change the form style number and session id if the params have changed
  // get the style from the url
  useEffect(() => {
    logger.log(params);
    const styleNumber = params.styleNumber;
    const sessionId = params.sessionId;
    if (styleNumber) {
      form.setValues({ ...form.values, sessionId: styleNumber });
    }
  }, [params]);

  // Custom localStorage hook implementation
  const getStoredSettings = () => {
    try {
      const stored = localStorage.getItem(`op-approval-display-settings-${assetType}`);

      logger.log('---------', stored, "stored");
      if (stored) {
        var parsed = JSON.parse(stored);
        parsed.isCommenting = allowCommenting;

        logger.log('---------', parsed, "parsed");
        // Merge with defaults to ensure all properties exist
        return {
          layoutType: ApprovalLayoutTypes.SINGLE,
          infoOpen: true,
          filterOpen: true,
          thumbnailSize: 150,
          isCommenting: allowCommenting,
          sortBy: "actionSort",
          sortDirection: "asc",
          syncZoomPan: true,
          bottomTrayOpen: true,
          annotationType: "PAN",
          ...parsed,
        };
      }
    } catch (error) {
      logger.log("Error reading localStorage:", error);
    }
    // Return defaults if no stored value or error
    return {
      layoutType: ApprovalLayoutTypes.SINGLE,
      infoOpen: false,
      filterOpen: false,
      thumbnailSize: 150,
      isCommenting: allowCommenting,
      sortBy: "actionSort",
      sortDirection: "asc",
      syncZoomPan: true,
      bottomTrayOpen: true,
      annotationType: "PAN",
    };
  };

  const [approvalSettings, setApprovalSettingsState] =
    useState(getStoredSettings);

  const setApprovalSettings = useCallback(
    (newSettings) => {
      const updatedSettings =
        typeof newSettings === "function"
          ? newSettings(approvalSettings)
          : newSettings;

      try {
        localStorage.setItem(
          `op-approval-display-settings-${assetType}`,
          JSON.stringify(updatedSettings)
        );
        setApprovalSettingsState(updatedSettings);
        logger.log("Settings saved to localStorage:", updatedSettings);
      } catch (error) {
        logger.log("Error saving to localStorage:", error);
        setApprovalSettingsState(updatedSettings);
      }
    },
    [approvalSettings]
  );

  // Debug logging to see what's happening with localStorage
  useEffect(() => {
    logger.log("AssetViewer mounted, approvalSettings:", approvalSettings);
    logger.log(
      "localStorage value:",
      localStorage.getItem("op-approval-display-settings")
    );
  }, []);

  useEffect(() => {
    logger.log("approvalSettings changed:", approvalSettings);
  }, [approvalSettings]);

  const filterMedia = useCallback(
    (vals) => {
      if (style && style.assets) {
        var newData = utils.filterMedia(style.assets, vals);

        setAssets(newData);
        // onFilterChange(vals);
        // setApprovalSettings({ ...approvalSettings, filter: vals });
      }
    },
    [approvalFilter, style]
  );

  const [selectedItem, setSelectedItem] = useState({
    ...style?.assets?.[index],
    // id: style.assets?.[index]?.assignedSequence,
  });
  const [selectedItems, setSelectedItems] = useState([style?.assets?.[index]]);
  const imageLocHash = useRef({});
  const { toggle } = useFullscreen();
  const [searchParams, setSearchParams] = useSearchParams();
  const mainPanelRef = useRef();
  // Use refs to track the latest values to avoid closure issues
  const styleAssetsRef = useRef(style?.assets);
  const selectedItemRef = useRef(selectedItem);
  // const [panelOpen, setPanelOpen] = useLocalStorage({
  //   key: "capture-viewer-panel-open",
  //   defaultValue: "true",
  // });

  useEffect(() => {
    if (mainPanelRef.current) {
      mainPanelRef.current.focus();
    }
  }, []);

  useEffect(() => {
    logger.log(style.assets);

    // Update refs with latest values to avoid closure issues
    styleAssetsRef.current = style?.assets;

    const item = style?.assets?.find((val) => val.id === selectedItem?.id);

    if (item) {
      const updatedItem = { ...item };
      setSelectedItem(updatedItem);
      selectedItemRef.current = updatedItem;
    } else {
      selectedItemRef.current = selectedItem;
    }

    // update the comparison items if they match
    if (comparisonItems.length && style?.assets) {
      const cItems = comparisonItems.map((citem) => {
        // find it in the style.assets
        const item = style?.assets?.find((val) => val.id === citem.id);
        return {
          ...item,
        };
      });
      setComparisonItems(cItems);
    }
  }, [style.assets, selectedItem?.id]);

  const toggleRef = useRef();

  useEffect(() => {
    // Ensure the referenced div exists
    const toggleDiv = toggleRef.current;
    const controlDiv = document.getElementById("openSeaDragon-controller");

    if (controlDiv && toggleDiv) {
      const firstChild = controlDiv.firstElementChild;

      if (firstChild) {
        firstChild.appendChild(toggleDiv);
      }
    }
  }, []);

  /**
   * Handler for image click
   */
  const onImageControlClick = useCallback(
    (type) => {
      logger.log("IMAGEW ZOOM CLICK");
      switch (type) {
        case ApprovalImageToolbarModes.ZOOM_RESET:
        case ApprovalImageToolbarModes.ZOOM_OUT:
        case ApprovalImageToolbarModes.ZOOM_IN: {
          let props = { ...imageControlSettings, zoomDirection: type };
          setImageControlSettings(props);
          break;
        }
        default: {
          break;
        }
      }
    },
    [imageControlSettings]
  );
  // Hotkey handler for zoom controls
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle hotkeys when not in an input field
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      ) {
        return;
      }

      switch (event.key) {
        case "+":
        case "=": // Handle both + and = keys (same key on many keyboards)
          event.preventDefault();
          onImageControlClick(ApprovalImageToolbarModes.ZOOM_IN);
          break;
        case "-":
          event.preventDefault();
          onImageControlClick(ApprovalImageToolbarModes.ZOOM_OUT);
          break;
        case "0":
          event.preventDefault();
          onImageControlClick(ApprovalImageToolbarModes.ZOOM_RESET);
          break;
        default:
          break;
      }
    };

    // Add event listener
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onImageControlClick]);

  const imageHandler = (type, data, originalEvent) => {
    var cItems, items;
    // Get the captures array - consistent with BottomTray mapping
    const captures = style.assets.map((val) => ({
      ...val,
      previewMid: val.preview,
      previewHi: val.preview,
      previewLo: val.thumb,
      id: val.id || val.assignedSequence,
    }));

    switch (type) {
      case CaptureEventType.IMAGE_CLICK: {
        logger.log(comparisonItems, originalEvent);

        if (originalEvent.metaKey) {
          // if command held down we add the item to the array
          // if it's not yet selected.
          const obj = selectedItems.find((val) => val.id === data.id);
          // if the object is already selected then we unselect it
          if (obj) {
            items = selectedItems.filter((val) => val.id !== data.id);
            cItems = comparisonItems.filter((val) => val.id !== data.id);
          } else {
            items = [...selectedItems, data];
            cItems = [...comparisonItems];
          }
        } else if (originalEvent.shiftKey) {
          // if shift key we select range from last selected item to current
          let lastObj =
            selectedItems && selectedItems.length > 0
              ? selectedItems[selectedItems.length - 1]
              : null;
          if (lastObj) {
            let lastIdx = captures.findIndex((val) => val.id === lastObj.id);
            let currIdx = captures.findIndex((val) => val.id === data.id);

            // Ensure we have valid indices
            if (lastIdx !== -1 && currIdx !== -1) {
              // Calculate the range (inclusive of both endpoints)
              let startIdx = Math.min(lastIdx, currIdx);
              let endIdx = Math.max(lastIdx, currIdx);
              let rangeItems = captures.slice(startIdx, endIdx + 1);

              // Combine existing selection with new range, removing duplicates
              let allItems = [...selectedItems, ...rangeItems];
              items = allItems.filter(
                (item, index, self) =>
                  index === self.findIndex((t) => t.id === item.id)
              );
            } else {
              items = [data];
            }
          } else {
            items = [data];
          }
          cItems = [...comparisonItems];
        } else {
          items = [data];

          // if no comparison items is selected
          if (comparisonItems && comparisonItems.length) {
            cItems = [...comparisonItems];
            
            // In SINGLE mode, always update comparisonItems[0] (or selectedCompareFrame)
            if (approvalSettings.layoutType === ApprovalLayoutTypes.SINGLE) {
              const frameIndex = selectedCompareFrame !== undefined && selectedCompareFrame !== null 
                ? selectedCompareFrame 
                : 0;
              // Prevent updating with the same image
              if (comparisonItems[frameIndex]?.id !== data.id) {
                cItems[frameIndex] = data;
                // Ensure array has at least one item
                if (cItems.length === 0) {
                  cItems = [data];
                }
              } else {
                // If it's the same image, don't update comparison items
                cItems = [...comparisonItems];
              }
            } else if (comparisonItems.length === 1) {
              // COMPARE mode: Prevent adding the same image as the first one
              if (comparisonItems[0]?.id !== data.id) {
                cItems[1] = data;
              } else {
                // If it's the same image, don't update comparison items
                cItems = [...comparisonItems];
              }
            } else if (
              selectedCompareFrame !== undefined &&
              selectedCompareFrame !== null
            ) {
              // COMPARE mode: Prevent replacing with the same image that's in the other frame
              const otherFrameIndex = selectedCompareFrame === 0 ? 1 : 0;
              if (comparisonItems[otherFrameIndex]?.id !== data.id) {
                cItems[selectedCompareFrame] = data;
              } else {
                // If it's the same image, don't update comparison items
                cItems = [...comparisonItems];
              }
            } else {
              // When replacing the first frame, check it's not the same as the second
              if (comparisonItems.length > 1 && comparisonItems[1]?.id === data.id) {
                // If it's the same image, don't update comparison items
                cItems = [...comparisonItems];
              } else {
                cItems[0] = data;
              }
            }
          } else {
            cItems = [data];
          }

          setSelectedItem({ ...data });
        }

        if (items) {
          setSelectedItems(items);
        }
        if (cItems) {
          setComparisonItems(cItems);
        }

        break;
      }
      case ApprovalEventTypes.COMPARE_FRAME_CLICK: {
        setSelectedCompareFrame(data.index);
        // if the selected fram is different from the selected item then set the selected item to the selected frame
        if (data.index !== selectedItem.id) {
          setSelectedItem(comparisonItems[data.index]);
        }
        break;
      }
      default: {
        break;
      }
    }
    onImageEvent(type, data, originalEvent);
  };

  // const onPanelToggleClick = () => {
  //   setPanelOpen(!panelOpen);
  // };

  const settingsChange = (type, data) => {
    logger.log(type, data);
    switch (type) {
      case ApprovalEventTypes.SETTINGS_CHANGE: {
        setApprovalSettings((prevSettings) => ({ ...prevSettings, ...data }));
        break;
      }
      default: {
        break;
      }
    }

    let urlParms = Object.fromEntries([...searchParams]);
    // const urlParms = new URLSearchParams(window.location.search);
    logger.log(urlParms, " PARMS CHANGE");
    if (data.layoutType !== urlParms.layoutType) {
      // set the settings as part of the url.
      searchParams.set("layoutType", data.layoutType);

      urlParms.layoutType = data.layoutType;

      setSearchParams(urlParms);
    }
  };

  const form = useForm({
    initialValues: {
      client: "",
      sessions: "",
      // sessionId: `${style?.id}`,
      sessionId: "",
      unpinned: false,
      star: "-1",
      sak: "-1",
      tags: [],
      machineId: "",
      styleNumber: "",
      colourTag: [],
      starComparison: ComparisonOperators.EQUAL,
    },

    validate: {
      // client: (value) => (value.length ? null : 'Please select a client'),
    },
  });

  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    // update the form values with the approvalFilter if they have changed
    form.setValues({ ...approvalFilter });
  }, [approvalFilter]);

  const layout = () => {
    switch (approvalSettings.layoutType) {
      case ApprovalLayoutTypes.SINGLE:
        return (
          <SingleLayout
            selectedAnnotation={selectedAnnotation}
            user={user}
            onMarkupChange={(type, data) =>
              handleMarkupChange(type, data, null)
            }
            image={selectedItem}
            showSettings={showSettings}
            showToolbar={true}
            settings={approvalSettings}
            onCaptureChange={onCaptureChange}
            settingsChange={settingsChange}
          />
        );
      case ApprovalLayoutTypes.COMPARE:
        return (
          <CompareLayout
            onImageEvent={imageHandler}
            captures={assets}
            selectedItems={selectedItems}
            comparisonItems={comparisonItems}
            selectedFrame={selectedCompareFrame}
            onCaptureChange={onCaptureChange}
            settings={approvalSettings}
            imageLocHash={imageLocHash}
            settingsChange={settingsChange}
            user={user}
            onMarkupChange={(type, data) =>
              handleMarkupChange(type, data, null)
            }
            showSettings={showSettings}
            selectedAnnotation={selectedAnnotation}
          />
        );
      case ApprovalLayoutTypes.COMMENT:
        return (
          <CommentLayout
            user={user}
            onSettingsChange={settingsChange}
            settings={approvalSettings}
            image={selectedItem}
            onImageEvent={imageHandler}
            captures={assets}
            selectedItems={selectedItems}
            comparisonItems={comparisonItems}
            selectedFrame={selectedCompareFrame}
            imageControlSettings={imageControlSettings}
            onCaptureChange={onCaptureChange}
            imageLocHash={imageLocHash}
            onMarkupChange={onMarkupChange}
          />
        );
      default:
        return <SingleLayout />;
    }
  };

  const onCommentClick = (annotation) => {
    logger.log(annotation, "annotation");
    setSelectedAnnotation(annotation);
  };

  // Handle markup changes including selection/deselection
  const handleMarkupChange = useCallback(
    (type, data, dataItem) => {
      logger.log(type, data, "markup change");
      const id = data?.payload?.markup?.id ?? data?.payload?.id;

      // Always read from refs to get latest values (avoid closure issues)
      const latestAssets = styleAssetsRef.current || style?.assets;
      const latestSelectedItem = selectedItemRef.current;

      const itemId = data?.image?.id || dataItem?.id || latestSelectedItem?.id;

      // Find the current item from latest assets or fallback to selectedItem ref
      // const currentItem =
      //   latestAssets?.find((val) => val.id === itemId) ||
      //   (latestSelectedItem?.id === itemId ? latestSelectedItem : null);


      //  const currentItem =(latestSelectedItem?.id === itemId ? latestSelectedItem : null);

      const currentItem = latestSelectedItem??null;

      // Get annotations - for UPDATE_MARKUP, optimistically update before searching
      let annotations = currentItem?.annotations || [];
      if (
        type === MarkupEventTypes.UPDATE_MARKUP &&
        currentItem &&
        data?.payload?.markup
      ) {
        const annotationIndex = annotations.findIndex(
          (a) =>
            a.id === id ||
            a.payload?.markup?.id === id ||
            a.payload?.payload?.markup?.id === id
        );

        if (annotationIndex >= 0) {
          annotations = [...annotations];
          annotations[annotationIndex] = {
            ...annotations[annotationIndex],
            payload: {
              ...annotations[annotationIndex].payload,
              markup: data.payload.markup,
            },
          };

          // Update selectedItem state and ref optimistically
          if (latestSelectedItem?.id === itemId) {
            const updatedItem = { ...latestSelectedItem, annotations };
            setSelectedItem(updatedItem);
            selectedItemRef.current = updatedItem;
          }
        }
      }

      // Find the annotation model
      const annoationModel = annotations.find(
        (a) =>
          a.id === id ||
          a.payload?.markup?.id === id ||
          a.payload?.payload?.markup?.id === id
      );

      // Forward to parent handler
      onMarkupChange?.(type, data);

      // Handle selection/deselection locally
      switch (type) {
        case MarkupEventTypes.SELECT_MARKUP:
          setSelectedAnnotation(annoationModel);
          break;
        case MarkupEventTypes.DESELECT_MARKUP:
          setSelectedAnnotation(null);
          break;
        case MarkupEventTypes.ADD_MARKUP: {
          const model = {
            annPart: style.stylePart,
            annSort: style.styleSort,
            payload: { markup: data.payload },
            ts: Date.now(),
            user: user.email,
            id: null,
          };
          setSelectedAnnotation(model);
          break;
        }
      }
    },
    [onMarkupChange, style, user]
  );

  logger.log(sessions, "sessions", selectedItem);
  return (
    <Box
      px="lg"
      pt="lg"
      className={`${classes.panel} ${
        approvalSettings.infoOpen ? classes["info-open"] : ""
      } ${approvalSettings.filterOpen ? classes["filter-open"] : ""}`}
    >
      <Group
        justify="space-between"
        align="center"
        pb="lg"
        px={"lg"}
        className={classes.header}
      >
        <Group justify="space-between">
          <UnstyledButton
            onClick={() => {
              onClose();
            }}
          >
            <Group>
              <IconArrowLeft
                size={25}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
              <Group>
                <Title order={5} className="unhightable">
                  Approvals
                </Title>
                /
                <Title order={5} className="unhightable">
                  Production
                </Title>
                /
                <Title order={5} className="unhightable">
                  {style.name}
                </Title>
              </Group>
            </Group>
          </UnstyledButton>

          {/* <Title className="main-content-heading" order={5}>
              {style.name}
            </Title> */}
          <PrimaryBadge>{style.styleNumber}</PrimaryBadge>
        </Group>
        <Group>
          <ActionIcon onClick={toggle} variant="default">
            <IconArrowsMaximize
              size={AppConstants.ICON_SIZE}
              stroke={AppConstants.ICON_STROKE_WIDTH}
            />
          </ActionIcon>
          <CloseButton onClick={onClose} />
        </Group>
      </Group>

      {selectedItem && selectedItem.preview ? (
        <Group
          wrap="nowrap"
          ref={mainPanelRef}
          align="flex-start"
          style={{ height: "100%", flexGrow: 1 }}
          gap={0}
          className={classes.seaDragon}
        >
          <div
            style={{ width: "100%", flexGrow: 1, alignSelf: "stretch" }}
            className={classes.filterPanel}
          >
            <FilterPanel
              approvalFilter={approvalFilter}
              onCollectionClick={() => {}}
              form={form}
              aggData={null}
              onSubmit={() => {
                logger.log("onSubmit");
                // set the filter.
                // setApprovalSettings({ ...approvalSettings, filter: form.values });
                filterMedia(form.values);
                onFilterChange(form.values);
              }}
              onClearClick={() => {
                filterMedia(new MediaFilterModel());
                onFilterChange(new MediaFilterModel());
              }}
              isSearching={isSearching}
              styles={[style]}
              sessions={sessions}
              approvalType={approvalType}
              formState={true}
              showFilters={showFilters}
              isLoading={sessions ? false : true}
            />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "calc(100vh - 72px)",
              gap: 0,
            }}
          >
            <Box
              className={"scan-image-holder"}
              style={{
                cursor: "grab",
                height: "100%",
                minHeight: 300,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
              }}

              // onClick={() => setImageModalOpen(true)}
            >
              <Toolbar
                settingsChange={(type, data) => settingsChange(type, data)}
                settings={approvalSettings}
                selectedItems={selectedItems}
                onImageControlClick={onImageControlClick}
                selectedCompareFrame={selectedCompareFrame}
                onCaptureChange={onCaptureChange}
                showCaptureToolbar={assetType === "task"?false:true}
                layoutOptions={[
                  {
                    value: ApprovalLayoutTypes.SINGLE,
                    label: (
                      <Center>
                        <IconRectangle size={16} />
                        {/* <Box ml={10}>Code</Box> */}
                      </Center>
                    ),
                  },
                  {
                    value: ApprovalLayoutTypes.COMPARE,
                    label: <Center><IconSquareToggle size={16} /></Center>
                  },
                ]}
              />

              {/* <ResolverImage fit='contain' src={scan.url} width={'100%'} height={300} /> */}
              {selectedItem ? (
                <>{layout()}</>
              ) : // <SingleLayout
              //   image={selectedItem}
              //   showSettings={true}
              //   showToolbar={true}
              //   settings={{ layoutType: "" }}
              //   onCaptureChange={onCaptureChange}
              // />
              null}
            </Box>

            <BottomTray
              captures={assets.map((val) => ({
                ...val,
                previewMid: val.preview,
                previewHi: val.preview,
                previewLo: val.thumb,
                id: val.id || val.assignedSequence,
              }))}
              onImageEvent={imageHandler}
              selectedItems={selectedItems}
              comparisonItems={comparisonItems}
              selectedCompareFrame={selectedCompareFrame}
              imageLocHash={imageLocHash}
              onCaptureChange={onCaptureChange}
              showComapirsonSelection={
                approvalSettings.layoutType === ApprovalLayoutTypes.COMPARE
              }
              isSearching={false}
              searchCursor={null}
              loadNextPage={null}
              showLoading={false}
              showSettings={showSettings}
              bottomTrayOpen={approvalSettings.bottomTrayOpen}
              onBottomTrayOpenChange={(val) => {
                setApprovalSettings({
                  ...approvalSettings,
                  bottomTrayOpen: val,
                });
              }}
            />
          </div>
          <div
            style={{ width: "100%", flexGrow: 1, alignSelf: "stretch" }}
            className={classes.infoPanel}
          >
            <AssetInfoPanel
              user={user}
              selectedItem={comparisonItems[selectedCompareFrame]}
              style={style}
              onItemChange={onCaptureChange}
              showComments={showComments}
              onAnnotationEvent={handleMarkupChange}
              selectedAnnotation={selectedAnnotation}
              type={assetType}
            />
          </div>
        </Group>
      ) : (
        <Skeleton width={"100%"} height={"100vh"}></Skeleton>
      )}

      {/* <div className="btn-panel-toggle" ref={toggleRef}>
        <Tooltip
          color="dark"
          position="bottom"
            label={approvalSettings.infoOpen ? "Hide Info Panel" : "Show Info Panel"}
        >
          <ActionIcon variant="default" onClick={() => onPanelToggleClick()}>
            {approvalSettings.infoOpen ? (
              <IconLayoutSidebarRightCollapse
                size={AppConstants.ICON_SIZE}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
            ) : (
              <IconLayoutSidebarRightExpand
                size={AppConstants.ICON_SIZE}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
            )}
          </ActionIcon>
        </Tooltip>
      </div> */}
    </Box>
  );
};

export default AssetViewer;

export const ApprovalEventTypes = {
  SETTINGS_CHANGE: "settingsChange",
  COMPARE_FRAME_CLICK: "compareFrameClick",
};

export const SortFields = {
  JOB_ID: "sessionId",
  MACHINE_ID: "machineId",
  FILE_NAME: "originalFilename",
  RATING: "star",
  DATE: "actionSort",
};

export const CaptureSortFields = [
  { label: "Capture Date", value: SortFields.DATE },
  { label: "Session", value: SortFields.JOB_ID },
  { label: "Machine", value: SortFields.MACHINE_ID },
  { label: "File Name", value: SortFields.FILE_NAME },
  { label: "Rating", value: SortFields.RATING },
];
